export const PROXY_URL = 'https://98va6f1d--admin-db-proxy.functions.blink.new';

// ─── Safe result type ─────────────────────────────────────────────────────────

export interface SafeResult<T> {
  ok: true;
  data: T;
}
export interface SafeError {
  ok: false;
  error: string;
  data: never[];
}
export type DbResult<T> = SafeResult<T> | SafeError;

// ─── Proxy caller ─────────────────────────────────────────────────────────────

export async function proxyCall<T = any>(
  action: 'list' | 'listIds' | 'count' | 'get' | 'upsert' | 'delete' | 'deleteAll',
  table: string,
  payload?: any,
): Promise<DbResult<T>> {
  try {
    const res = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, table, payload }),
    });

    const json = await res.json().catch(() => ({ ok: false, error: `HTTP ${res.status}` }));

    if (!json.ok) {
      console.error(`[adminDb] ${action} ${table} failed:`, json.error);
      return { ok: false, error: json.error ?? 'Unknown error', data: [] };
    }

    return { ok: true, data: json.data as T };
  } catch (e: any) {
    console.error(`[adminDb] ${action} ${table} exception:`, e?.message);
    return { ok: false, error: e?.message ?? 'Network error', data: [] };
  }
}

// ─── DB Test ───────────────────────────────────────────────────────────────

export async function dbTestWriteRead(tableName: string): Promise<{ ok: boolean; message: string }> {
  const testId = `test_${Date.now()}`;
  const now = new Date().toISOString();

  const testPayloads: Record<string, Record<string, unknown>> = {
    cmsBlocks: { pageKey: 'test', blockKey: 'test', lang: 'ru', contentJson: '{}', updatedAt: now },
    catalogProducts: { series: 'test', slug: 'test-slug', nameJson: '{}', image: '', palette: '', colorTag: '', groupType: '', installationType: '', layingTypesJson: '[]', collection: '', compatibilityJson: '[]', code: 'TEST', specsJson: '{}', updatedAt: now },
    portfolioItems: { titleJson: '{}', cityJson: '{}', area: 0, productType: 'test', imagesJson: '[]', updatedAt: now },
    newsItems: { titleJson: '{}', excerptJson: '{}', bodyJson: '{}', coverImage: '', date: now, updatedAt: now },
    whereToBuyPoints: { title: 'Test Point', lat: 0, lng: 0, address: 'test', phone: '', type: 'dealer', metaJson: '{}', updatedAt: now },
  };

  const payload = testPayloads[tableName];
  if (!payload) return { ok: false, message: `Unknown table: ${tableName}` };

  const upsertResult = await proxyCall('upsert', tableName, { id: testId, ...payload });
  if (!upsertResult.ok) return { ok: false, message: `Upsert failed: ${upsertResult.error}` };

  const getResult = await proxyCall('get', tableName, { id: testId });
  if (!getResult.ok) return { ok: false, message: `Get failed: ${getResult.error}` };

  await proxyCall('delete', tableName, { id: testId });

  return { ok: true, message: `✓ Запись создана, прочитана и удалена (id: ${testId})` };
}
